import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Skeleton, Modal } from 'antd';
import { FileSearchOutlined, StarFilled } from '@ant-design/icons';
import { addOpacityMenu } from 'src/utils/colorHelpers';
import { connect } from 'react-redux';

import SvgPanZoom from './svgPanZoom';

const useStyles = createUseStyles({
	cardContainer: (props) => ({
		width: '192px',
		height: '242px',
		margin: '8px 4px',
		backgroundColor: `${addOpacityMenu(props.model_LeftBack)}`,
		border: `1px solid ${props.model_LeftBack}`,
		borderRadius: '10px 10px 10px 10px',
		textAlign: 'center',
		transition: '0.3s',
		'&:hover': {
			backgroundColor: `${props.model_LeftBack}`,
		},
	}),
	myskeleton: {
		width: 'auto',
		height: '176px',
	},
	canvas: {
		position: 'absolute',
		width: '240px',
		height: '230px',
		transition: '1.5s ease-out',
		zIndex: '10',
		backgroundColor: 'transparent',
		pointerEvents: 'none',
	},
	imageContainer: {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '184px',
		borderRadius: '10px 10px 0 0',
		backgroundColor: 'white',
		overflow: 'hidden',
		borderBottom: '2px solid #D8D8D8',
		'& .ant-skeleton-element': {
			'& .ant-skeleton-image': {
				width: '187px',
				height: '100%',
				backgroundColor: 'white',
			},
			'& .ant-skeleton-image-svg': {
				width: '77px',
				height: '77px',
			},
		},
	},
	image: {
		width: '100%',
	},
	dateParagraph: (props) => ({
		position: 'relative',
		fontSize: 'min(3vmax, 15px)',
		bottom: '16px',
		color: `${props.model_LeftText}`,
	}),
	titleOfCard: (props) => ({
		display: 'inline-block',
		fontSize: 'min(3vmax, 15px)',
		maxWidth: '182px',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		margin: '5px 0px 0px 0px',
		textOverflow: 'ellipsis',
		color: `${props.model_LeftText}`,
	}),
	openImageIcon: {
		position: 'relative',
		right: '1px',
		fontSize: '24px',
		color: 'black',
		opacity: '40%',
	},
	isModalOpenButton: {
		position: 'absolute',
		top: '10px',
		right: '10px',
		width: '40px',
		height: '40px',
		backgroundColor: 'white',
		padding: '10px',
		border: 'none',
		borderRadius: '40px',
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: '#F5F5F5',
		},
		'&:hover $openImageIcon': {
			opacity: '40%',
		},
	},
	buttonClose: {
		position: 'relative',
		width: '50px',
		height: '100%',
		backgroundColor: 'white',
		padding: '10px',
		cursor: 'pointer',
		zIndex: '13',
		margin: '0',
		transitionDuration: '300ms',
		'&:hover': {
			backgroundColor: '#ECECEC',
		},
		'&:hover $closeButton': {
			color: 'black',
		},
	},
	closeButton: {
		fontSize: '20px',
		color: 'grey',
		marginTop: '2px',
	},
	title: {
		position: 'relative',
		marginTop: '5px',
		marginLeft: '10px',
		padding: '0',
	},
	header: {
		display: 'flex',
		justifyContent: 'space-between',
		height: '45px',
		borderBottom: '2px solid #D8D8D8',
	},
	bigView: {
		height: '70vh',
		width: '100%',
	},
	modal: {
		maxWidth: '1000px',
	},
});

// used for image dimensions in bigDiv
const loadImage = (model, imageUrl) => {
	const img = new Image();
	img.src = imageUrl;

	img.onload = () => {
		const newModel = model;
		newModel.imgWidth = img.width;
		newModel.imgHeight = img.height;
	};
	img.onerror = (err) => {
		console.log('img error');
		console.error(err);
	};
};

const CustomModelSelectCard = (props) => {
	const { model, ImagesLoaded, settings } = props;
	const { menuColor: colors } = settings;
	const { i18n } = useTranslation();
	const classes = useStyles(colors);
	const [onLoad, setOnLoad] = useState(0); // 0 = isloading, 1 = image showing, 2 image not available
	const [onLoadDiv, setOnLoadDiv] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const svgPopupRef = useRef(null);
	const [width, setWidth] = useState(0);
	const [height, setHeight] = useState(0);
	const [dimensions, setDimensions] = useState({
		height: window.innerHeight,
		width: window.innerWidth,
	});

	const handleCancel = () => {
		setIsModalOpen(false);
		Modal.destroyAll();
	};

	// default image will be shown in case image not found
	function timedoutImageCall() {
		setOnLoad(2);
	}

	function handleResize() {
		setDimensions({
			height: window.innerHeight,
			width: window.innerWidth,
		});
	}

	// useEffect which adjusts the width and height of bigViewer when dimensions change
	useEffect(() => {
		if (isModalOpen) {
			handleResize();
			window.addEventListener('resize', handleResize);

			return () => {
				window.removeEventListener('resize', handleResize);
			};
		}
	}, [isModalOpen]);

	// useEffect to isModalOpen the bigDiv and svgViewer according to the isModalOpen of the window
	useEffect(() => {
		if (isModalOpen && svgPopupRef.current) {
			if (svgPopupRef.current.offsetWidth) {
				setWidth(svgPopupRef.current.offsetWidth);
				setHeight(svgPopupRef.current.offsetHeight - 50);
			}
			setOnLoadDiv(true);
		}
	}, [dimensions, isModalOpen]);

	return (
		<div className={classes.cardContainer}>
			<div id='containerId' className={classes.imageContainer}>
				<a href={`/model/${model.slug}/view/${model.homeView}`}>
					{onLoad === 0 && <Skeleton.Image className={classes.myskeleton} active size='large' />}
					{onLoad === 2 && <Skeleton.Image className={classes.myskeleton} size='large' />}
					{ImagesLoaded && (
						<img
							id='imageId'
							className={classes.image}
							src={`data:image/svg+xml;utf8,${model.img}`}
							onLoad={() => {
								setOnLoad(1);
								loadImage(model, `data:image/svg+xml;utf8,${model.img}`);
							}}
							onError={({ currentTarget }) => {
								const newCurrentTarget = currentTarget;
								newCurrentTarget.onerror = null;
								timedoutImageCall();
							}}
							alt={<Skeleton.Image className={classes.myskeleton} size='large' />}
							style={onLoad === 1 ? {} : { display: 'none' }}
						/>
					)}
				</a>
				{onLoad === 1 && (
					<button
						className={classes.isModalOpenButton}
						type='button'
						onClick={() => {
							setIsModalOpen(true);
						}}
					>
						<FileSearchOutlined className={classes.openImageIcon} />
					</button>
				)}
			</div>
			<a href={`/model/${model.slug}/view/${model.homeView}`}>
				<div>
					<b>
						<p className={classes.titleOfCard}>
							{model.displayTitle[i18n.language]}
							{!!model.isFavorite && (
								<StarFilled style={{ fontSize: '17px', position: 'relative', marginLeft: '5px' }} />
							)}
						</p>
					</b>
					<p className={classes.dateParagraph}>{model.latestVersionUpload.substring(0, 10)}</p>
				</div>
			</a>
			<Modal
				title={model.displayTitle[i18n.language]}
				className={classes.modal}
				centered
				open={isModalOpen}
				onCancel={handleCancel}
				width='70vw'
				footer={null}
			>
				<div ref={svgPopupRef} className={classes.bigView}>
					{onLoadDiv && (
						<div>
							<SvgPanZoom
								src={`data:image/svg+xml;utf8,${model.img}`}
								modelTitle={model.displayTitle[i18n.language]}
								refBigWidth={width}
								refBigHeight={height}
								imgWidth={model.imgWidth}
								imgHeight={model.imgHeight}
							/>
						</div>
					)}
				</div>
			</Modal>
		</div>
	);
};

const mapStateToProps = (state) => ({
	activeUser: state.activeUser,
	modelList: state.modelList,
	settings: state.settings,
});

export default connect(mapStateToProps)(CustomModelSelectCard);
